import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';

// Hooks
import useOktaWidget from './hooks/useOktaWidget';

import './LoginWidget.css';
import { Hidden } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Typography from 'components/CncUI/Typography';
import Button from 'components/CncUI/Button';
import { Link } from 'react-router-dom';
import ForwardIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Paper from '@material-ui/core/Paper';
import SupportFooter from '../SupportFooter'; // 134x46

// v2 icons
import CncCapsMsLogoV2 from 'images/login/login_cnc_caps_ms_logo.svg';
import LoginBankV2 from 'images/login/login_bank_v2.svg';
import LoginComputerV2 from 'images/login/login_computer_v2.svg';
import LoginProfileV2 from 'images/login/login_profile_v2.svg';
import LoginCalendar from 'images/login/login_calendar.svg';
import LoginProjectSetup from 'images/login/login_project_setup.svg';

const useStyles = makeStyles(({ palette, breakpoints }) => ({
  hideEmail: {
    '& a.email-button': {
      display: 'none !important',
    },
  },
  root: {
    maxWidth: 1180,
    margin: 'auto',
    [breakpoints.up('md')]: {
      paddingTop: 120,
      paddingBottom: 88,
    },
  },
  leftPanel: {
    backgroundImage: palette.cncUI.gradients.header,
    '& h3': {
      color: palette.cncUI.white,
    },
    '& p': {
      color: palette.cncUI.white,
    },
    color: palette.cncUI.white,
    textAlign: 'center',
    [breakpoints.up('md')]: {
      height: 640,
      boxShadow: '5px 2px 8px rgba(0, 0, 0, 0.2);',
      zIndex: 100,
    },
  },
  topPanel: {
    [breakpoints.down('sm')]: {
      height: 252,
      width: '100%',
    },
  },
  rightPanel: {
    padding: '6px 0',
    [breakpoints.down('sm')]: {
      padding: 0,
    },
    '& #okta-sign-in.no-beacon .auth-header': {
      [breakpoints.down('sm')]: {
        padding: 0,
      },
    },
  },
  paper: {
    borderRadius: 0,
    padding: '16px 16px 24px',
    overflow: 'auto',
    '-ms-overflow-style': 'none',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    [breakpoints.up('md')]: {
      height: 630,
      padding: 0,
    },
    '& .MuiFormControlLabel-label': {
      fontSize: 14,
      fontWeight: 400,
      color: palette.cncUI.black,
    },
  },
  innerLeft: {
    color: palette.cncUI.white,
    padding: '8px 16px',
    [breakpoints.down('sm')]: {
      width: '100%',
      margin: 0,
      padding: 0,
    },
    [breakpoints.up('md')]: {
      padding: '25px 40px',
    },
    '& h3': {
      marginTop: 40,
      [breakpoints.down('sm')]: {
        marginTop: 32,
      },
    },
    '& hr': {
      border: '1px solid #ffffff',
      marginTop: 17,
      [breakpoints.down('sm')]: {
        marginTop: 0,
        marginBottom: 24,
      },
    },
  },
  filter: {
    // https://codepen.io/sosuke/pen/Pjoqqp color SVG image
    // if your SVG isn't black, add brightness(0) saturate(100%) to the beginning of the list
    filter:
      'brightness(0) saturate(100%) invert(69%) sepia(16%) saturate(461%) hue-rotate(128deg) brightness(90%) contrast(95%)',
  },
  logoV2: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    margin: '0.75em 0',
    [breakpoints.down('sm')]: {
      margin: '28px 0',
    },
    '& img': {
      maxWidth: 300,
      maxHeight: 78,
      flex: 1,
    },
  },
  noHeight: {
    height: 0,
  },
  mobileExpand: {
    position: 'relative',
    backgroundColor: palette.cncUI.default['+6'],
    color: palette.cncUI.white,
    minHeight: 24,
    width: 24,
    height: 24,
    alignContent: 'center',
    justifyContent: 'center',
    top: -12,
    borderRadius: '50%',
    marginRight: 'auto',
    marginLeft: 'auto',
  },
  widgetV2: {
    placeSelf: 'center',
    animationName: 'loginOpacity',
    animationDuration: '0.3s',
    animationTimingFunction: 'ease-in',
    marginTop: 10,
    '& .auth-container': {
      margin: '0 auto !important',
      border: 'none !important',
      boxShadow: 'none !important',
      backgroundColor: 'transparent !important',
      overflowX: 'hidden !important',
    },
    '& #okta-sign-in .beacon-container': {
      width: 75,
      height: 75,
    },
    '& .okta-form-title': {
      fontSize: '24px !important',
      fontWeight: '400 !important',
    },
    '& .link': {
      fontSize: '14px !important',
      fontWeight: '400 !important',
      color: `${palette.cncUI.primary.gray.primary} !important`,
    },
    '& .o-form-button-bar': {
      padding: '0 !important',
    },
    '& .auth-content': {
      paddingTop: '0 !important',
    },
    '& .auth-container.main-container .margin-btm-30': {
      marginBottom: '10px !important',
    },
    '& .auth-header': {
      paddingTop: '24px !important',
    },
    '& .beacon-loading': {
      marginBottom: '16px !important',
    },
  },
  code: {
    position: 'absolute',
    right: 0,
    top: 0,
    maxWidth: 860,
    overflowX: 'auto',
    zIndex: 9999,
    '& span': {
      padding: 8,
      display: 'inline-block',
      color: palette.cncUI.default['+10'],
    },
    '& pre': {
      display: 'none',
      color: palette.cncUI.primary.gray.primary,
      padding: 8,
    },
    '&:hover': {
      background: palette.cncUI.default['+10'],
    },
    '&:hover span': {
      display: 'none',
    },
    '&:hover pre': {
      display: 'block',
    },
    [breakpoints.down('md')]: {
      display: 'none',
    },
  },
  register: {
    textDecoration: 'none',
    textTransform: 'none',
    [breakpoints.down('sm')]: {
      color: palette.cncUI.default.primary,
    },
  },
  registerButton: {
    marginTop: 14,
    [breakpoints.up('sm')]: {
      maxWidth: 500,
    },
    [breakpoints.down('sm')]: {
      marginBottom: 40,
      width: '60%',
    },
    [breakpoints.down('xs')]: {
      width: '95%',
    },
  },
  imgContainer: {
    display: 'flex',
    width: '100%',
    height: 60,
    justifyContent: 'center',
    alignItems: 'center',
    [breakpoints.down('sm')]: {
      height: 80,
      '& img': {
        width: 80,
        height: 'auto',
      },
    },
  },
  smallMargin: {
    marginTop: 16,
    marginBottom: 0,
  },
  noSpacing: {
    padding: '0 !important',
    marginTop: 0,
    marginBottom: 12,
    [breakpoints.down('sm')]: {
      marginTop: 40,
    },
  },
  sidePadding: {
    minWidth: 158,
    [breakpoints.down('sm')]: {
      marginTop: 16,
    },
    [breakpoints.down('xs')]: {
      padding: '0 79px',
    },
  },
  smallMarginBottom: {
    margin: 0,
    marginBottom: 10,
  },
  noMargin: {
    margin: 0,
  },
}));

const WelcomeLogin = props => {
  const { oktaConfig, smsRecovery, login, hideRegister, isMobile } = props;
  const isProd = process.env.REACT_APP_ENVIRONMENT === 'prod';
  const classes = useStyles();
  useOktaWidget('sign-in-widget', oktaConfig, login);

  const RegisterV2 = () => (
    <>
      <Typography
        variant="body1"
        size="short"
        italic
        className={classes.noMargin}
      >
        Need a MyCast&Crew account?
      </Typography>
      <Link className={classes.register} to="/register">
        <Button
          className={clsx(classes.register, classes.registerButton)}
          secondary
        >
          Start my registration <ForwardIcon />
        </Button>
      </Link>
    </>
  );

  const ClientOptions = () => (
    <Grid item xs={12}>
      <hr />
      <Grid item xs={12} className={classes.noSpacing}>
        <Typography
          variant="body1"
          size="short"
          italic
          style={{ marginTop: 32 }}
        >
          With your MyCast&Crew account, studio staff can
        </Typography>
      </Grid>
      <Grid container justifyContent="space-around" alignItems="center">
        <Grid item xs={12} sm={6}>
          <div className={classes.imgContainer}>
            <img src={LoginProjectSetup} alt="adjustments image" />
          </div>
          <Typography variant={isMobile ? 'caption' : 'body1'} size="short">
            Set up new projects
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <div className={classes.imgContainer}>
            <img src={LoginCalendar} alt="adjustments image" />
          </div>
          <Typography variant={isMobile ? 'caption' : 'body1'} size="short">
            Submit adjustments
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );

  const ServiceDescription = () => (
    <>
      <Grid item xs={12} className={classes.noSpacing}>
        <Typography variant="body1" size="short" italic>
          Log in to your MyCast&Crew account to
        </Typography>
      </Grid>
      <Grid item xs={12} sm={4}>
        <div className={classes.imgContainer}>
          <img src={LoginProfileV2} alt="profile image" />
        </div>
        <Typography
          variant={isMobile ? 'caption' : 'body1'}
          size="short"
          className={classes.sidePadding}
        >
          Manage your profile across all our applications
        </Typography>
      </Grid>
      <Grid item xs={12} sm={4}>
        <div className={classes.imgContainer}>
          <img src={LoginBankV2} alt="bank image" />
        </div>
        <Typography
          variant={isMobile ? 'caption' : 'body1'}
          size="short"
          className={classes.sidePadding}
        >
          Set up and manage your direct deposit
        </Typography>
      </Grid>
      <Grid item xs={12} sm={4}>
        <div className={classes.imgContainer}>
          <img src={LoginComputerV2} alt="computer image" />
        </div>
        <Typography
          variant={isMobile ? 'caption' : 'body1'}
          size="short"
          className={classes.sidePadding}
        >
          Access pay stubs, tax documents, digital onboards, and timecards
        </Typography>
      </Grid>
      <ClientOptions />
    </>
  );

  const loginPaneProps = { justifyContent: 'space-between' };

  return (
    <div className={clsx(smsRecovery && classes.hideEmail)}>
      {!isProd && (
        <div className={classes.code}>
          <span className={classes.anchor}>View okta configuration</span>
          <pre>{JSON.stringify(oktaConfig, null, 2)}</pre>
        </div>
      )}
      <Grid
        container
        spacing={0}
        className={classes.root}
        data-test-id="WelcomeLogin"
      >
        <Grid
          item
          xs={12}
          md={6}
          className={clsx(classes.leftPanel, classes.topPanel)}
        >
          <Grid
            container
            spacing={3}
            direction="row"
            className={classes.innerLeft}
          >
            <Grid item xs={12}>
              <div className={classes.logoV2}>
                <img src={CncCapsMsLogoV2} alt="Cast&Crew CAPS MS Logo" />
              </div>
              <Typography variant="h3">Welcome to MyCast&Crew</Typography>
            </Grid>
            <Hidden smDown>
              <ServiceDescription />
            </Hidden>
          </Grid>
        </Grid>
        <Hidden mdUp>
          <Grid item xs={12} className={classes.noHeight}>
            <div className={classes.mobileExpand}>
              <ExpandMoreIcon />
            </div>
          </Grid>
        </Hidden>
        <Grid item xs={12} md={6} className={classes.rightPanel}>
          <Paper className={classes.paper}>
            <Grid
              container
              alignItems="center"
              justifyContent="space-between"
              style={{ height: '100%', overflowY: 'auto' }}
            >
              <Grid
                item
                xs={12}
                container
                alignItems="center"
                justifyContent="center"
                direction="column"
                spacing={0}
                {...loginPaneProps}
              >
                {['prod', 'demo', 'extuat'].includes(
                  process.env.REACT_APP_ENVIRONMENT,
                ) && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html:
                        '<img src="https://d21y75miwcfqoq.cloudfront.net/192cb88e" style="position: absolute" referrerpolicy="no-referrer-when-downgrade" />',
                    }}
                  />
                )}
                <div className={classes.widgetV2} id="sign-in-widget" />
              </Grid>
              {!hideRegister && (
                <Grid
                  item
                  xs={12}
                  container
                  justifyContent="center"
                  alignItems="center"
                  direction="column"
                >
                  <RegisterV2 />
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
        <Hidden mdUp>
          <Grid item xs={12} className={classes.noHeight}>
            <div className={classes.mobileExpand}>
              <ExpandMoreIcon />
            </div>
          </Grid>
          <Grid item xs={12} md={6} className={classes.leftPanel}>
            <Grid
              container
              spacing={3}
              direction="row"
              className={classes.innerLeft}
            >
              <ServiceDescription />
            </Grid>
          </Grid>
        </Hidden>
        <SupportFooter />
      </Grid>
    </div>
  );
};

export default WelcomeLogin;
